import { useState } from 'react'
import { motion } from 'framer-motion'
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { supabase } from '../config/supabase'

export default function EmailCapture({ t, language }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    segment: '',
    wouldPay: false,
    wantInterview: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      // Check if Supabase is configured
      if (!supabase) {
        setError(language === 'ja' 
          ? '⚠️ データベースが未設定です。.envファイルにSupabase認証情報を追加してください。' 
          : '⚠️ Database not configured. Add Supabase credentials to .env file.')
        setIsSubmitting(false)
        return
      }

      // Save to Supabase
      const { data, error: supabaseError } = await supabase
        .from('genkidesu_signups')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            segment: formData.segment,
            would_pay: formData.wouldPay,
            want_interview: formData.wantInterview,
            language: language,
            created_at: new Date().toISOString(),
            utm_source: new URLSearchParams(window.location.search).get('utm_source'),
            utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
            utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign')
          }
        ])
        .select()

      if (supabaseError) throw supabaseError

      setIsSuccess(true)
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          segment: '',
          wouldPay: false,
          wantInterview: false
        })
        setIsSuccess(false)
      }, 5000)

    } catch (err) {
      console.error('Error submitting form:', err)
      setError(language === 'ja' 
        ? 'エラーが発生しました。もう一度お試しください。' 
        : 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <section id="email-capture" className="section-padding">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card text-center"
          >
            <CheckCircleIcon className="h-20 w-20 text-primary-500 mx-auto mb-6" />
            <h3 className="text-3xl font-bold text-neutral-900 mb-4">
              {t.emailCapture.form.success}
            </h3>
            <p className="text-neutral-600">
              {language === 'ja' 
                ? 'すぐにメールでご連絡いたします。' 
                : 'We\'ll reach out to you soon via email.'}
            </p>
          </motion.div>
        </div>
      </section>
    )
  }

  return (
    <section id="email-capture" className="section-padding">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-5xl font-display font-bold text-neutral-900 mb-4">
            {t.emailCapture.title}
          </h2>
          <p className="text-xl text-neutral-600 mb-3">
            {t.emailCapture.subtitle}
          </p>
          <p className="text-lg text-primary-600 font-medium">
            {t.emailCapture.launchDate}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
                {t.emailCapture.form.name} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="input-field"
                placeholder={language === 'ja' ? '山田太郎' : 'Taro Yamada'}
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                {t.emailCapture.form.email} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="input-field"
                placeholder="example@email.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-2">
                {t.emailCapture.form.phone}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="input-field"
                placeholder={language === 'ja' ? '090-1234-5678' : '090-1234-5678'}
              />
            </div>

            {/* Segment */}
            <div>
              <label htmlFor="segment" className="block text-sm font-medium text-neutral-700 mb-2">
                {t.emailCapture.form.segment} *
              </label>
              <select
                id="segment"
                name="segment"
                required
                value={formData.segment}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">
                  {language === 'ja' ? '選択してください' : 'Select one...'}
                </option>
                <option value="solo">{t.emailCapture.form.segments.solo}</option>
                <option value="traveler">{t.emailCapture.form.segments.traveler}</option>
                <option value="professional">{t.emailCapture.form.segments.professional}</option>
                <option value="other">{t.emailCapture.form.segments.other}</option>
              </select>
            </div>

            {/* Intent Questions */}
            <div className="bg-primary-50 rounded-2xl p-6 border-2 border-primary-200 space-y-4">
              <p className="font-bold text-neutral-900 mb-4">
                {language === 'ja' ? 'あなたの興味について教えてください' : 'Tell us about your interest'}
              </p>
              
              {/* Would Pay Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="wouldPay"
                  checked={formData.wouldPay}
                  onChange={handleChange}
                  className="mt-1 h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <span className="text-neutral-800">
                  {t.emailCapture.form.wouldPay}
                </span>
              </label>
              
              {/* Want Interview Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="wantInterview"
                  checked={formData.wantInterview}
                  onChange={handleChange}
                  className="mt-1 h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <span className="text-neutral-800">
                  {t.emailCapture.form.wantInterview}
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-red-700">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t.emailCapture.form.submitting}
                </>
              ) : (
                <>
                  <EnvelopeIcon className="h-6 w-6" />
                  {t.emailCapture.form.submit}
                </>
              )}
            </button>

            {/* Privacy Note */}
            <p className="text-xs text-center text-neutral-500">
              {language === 'ja' 
                ? 'あなたのメールアドレスは安全に保管され、第三者と共有されることはありません。' 
                : 'Your email is safe with us and will never be shared with third parties.'}
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  )
}
